
import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  demographicsSchema,
  DemographicsFormValues,
} from "@/components/profile/demographics/types";
import { AgeGenderFields } from "@/components/profile/demographics/AgeGenderFields";
import { EducationEmploymentFields } from "@/components/profile/demographics/EducationEmploymentFields";
import { PoliticalIncomeFields } from "@/components/profile/demographics/PoliticalIncomeFields";
import { ResidenceField } from "@/components/profile/demographics/ResidenceField";
import { Form } from "@/components/ui/form";
import { Json } from "@/integrations/supabase/types";

// For use in CreateUserDialog - props are slightly different than for Edit
interface Props {
  demographics: Json | null | undefined;
  setDemographics: (data: DemographicsFormValues) => void;
}

const CreateUserDemographicsFields: React.FC<Props> = ({
  demographics,
  setDemographics,
}) => {
  // Convert from Json type to DemographicsFormValues
  const getDemographicsData = (): DemographicsFormValues | null => {
    if (!demographics) return null;
    if (typeof demographics === 'object' && demographics !== null) {
      return demographics as unknown as DemographicsFormValues;
    }
    return null;
  };

  const demographicsData = getDemographicsData();

  const form = useForm<DemographicsFormValues>({
    resolver: zodResolver(demographicsSchema),
    defaultValues: {
      age_group: "",
      gender: "",
      education_level: "",
      employment_status: "",
      political_leaning: "",
      income_range: "",
      residence_type: "",
      ...demographicsData,
    },
    mode: "onBlur",
  });

  // Sync fields with latest prop if dialog is reset
  useEffect(() => {
    const data = getDemographicsData();
    if (data) {
      form.reset(data);
    } else {
      form.reset({
        age_group: "",
        gender: "",
        education_level: "",
        employment_status: "",
        political_leaning: "",
        income_range: "",
        residence_type: "",
      });
    }
    // eslint-disable-next-line
  }, [demographics]);

  // Update parent on any change
  useEffect(() => {
    const subscription = form.watch((latest) => {
      setDemographics({ ...latest });
    });
    return () => subscription.unsubscribe();
    // eslint-disable-next-line
  }, [form.watch, setDemographics]);

  return (
    <Form {...form}>
      <div className="border p-3 rounded-md bg-gray-50 mb-2">
        <div className="font-semibold mb-2 text-sm text-gray-600">Demographic Data</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <AgeGenderFields control={form.control} />
          <EducationEmploymentFields control={form.control} />
          <PoliticalIncomeFields control={form.control} />
          <ResidenceField control={form.control} />
        </div>
      </div>
    </Form>
  );
};

export default CreateUserDemographicsFields;
